// Reset password page — landed da email link con ?token=X.
// Form: nuova password + conferma → POST /auth/reset-password.
// Su successo: redirect /login con flash success.
import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { resetPassword } from '../lib/api'

type ResetSearch = {
  token?: string
}

export const Route = createFileRoute('/reset-password')({
  validateSearch: (search: Record<string, unknown>): ResetSearch => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: ResetPasswordPage,
})

function ResetPasswordPage() {
  const search = Route.useSearch()
  const navigate = useNavigate()
  const token = search.token ?? ''
  const [pw1, setPw1] = useState('')
  const [pw2, setPw2] = useState('')
  const [done, setDone] = useState(false)

  const mut = useMutation<{ message: string }, Error, void>({
    mutationFn: () => resetPassword(token, pw1),
    onSuccess: () => {
      setDone(true)
    },
  })

  const noToken = !token
  const pwMismatch = pw1 !== pw2
  const pwShort = pw1.length < 8
  const canSubmit = !noToken && !pwMismatch && !pwShort && !mut.isPending

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        padding: 'var(--sp-4)',
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-5)',
          maxWidth: 460,
          width: '100%',
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
        }}
      >
        <h1 style={{ margin: 0, marginBottom: 6, fontSize: 22 }}>
          Reset password
        </h1>
        <p className="muted" style={{ marginTop: 0, marginBottom: 'var(--sp-3)', fontSize: 13 }}>
          MB Aste · AGL Roma EUR
        </p>

        {noToken && (
          <div
            className="error"
            style={{
              padding: 'var(--sp-2) var(--sp-3)',
              background: 'var(--risk-soft)',
              color: 'var(--risk-ink)',
              borderRadius: 'var(--r-sm)',
              marginBottom: 'var(--sp-3)',
              fontSize: 12,
            }}
          >
            Token mancante. Usa il link ricevuto via email.
          </div>
        )}

        {done ? (
          <>
            <div
              style={{
                padding: 'var(--sp-3)',
                background: 'var(--ok-soft)',
                color: 'var(--ok-ink)',
                borderRadius: 'var(--r-sm)',
                marginBottom: 'var(--sp-3)',
                fontSize: 13,
              }}
            >
              ✓ Password aggiornata. Effettua il login.
            </div>
            <button
              type="button"
              className="btn primary"
              style={{ width: '100%' }}
              onClick={() => void navigate({ to: '/login' })}
            >
              Vai al login
            </button>
          </>
        ) : (
          <>
            <div className="form-field">
              <label>Nuova password (minimo 8 caratteri)</label>
              <input
                type="password"
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                disabled={mut.isPending || noToken}
                autoFocus
              />
            </div>
            <div className="form-field">
              <label>Conferma password</label>
              <input
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                disabled={mut.isPending || noToken}
              />
            </div>

            {pw1 && pwShort && (
              <div
                className="muted"
                style={{ fontSize: 11, color: 'var(--risk-ink)' }}
              >
                ⚠ Password minimo 8 caratteri
              </div>
            )}
            {pw2 && pwMismatch && (
              <div
                className="muted"
                style={{ fontSize: 11, color: 'var(--risk-ink)' }}
              >
                ⚠ Le password non coincidono
              </div>
            )}

            {mut.isError && (
              <div
                className="error"
                style={{
                  padding: 'var(--sp-2) var(--sp-3)',
                  background: 'var(--risk-soft)',
                  color: 'var(--risk-ink)',
                  borderRadius: 'var(--r-sm)',
                  marginTop: 'var(--sp-2)',
                  fontSize: 12,
                }}
              >
                {mut.error?.message ?? 'Errore reset password'}
              </div>
            )}

            <button
              type="button"
              className="btn primary"
              style={{ width: '100%', marginTop: 'var(--sp-3)' }}
              onClick={() => mut.mutate()}
              disabled={!canSubmit}
            >
              {mut.isPending ? 'Aggiornamento…' : 'Imposta nuova password'}
            </button>

            <button
              type="button"
              className="btn ghost"
              style={{ width: '100%', marginTop: 'var(--sp-2)' }}
              onClick={() => void navigate({ to: '/login' })}
              disabled={mut.isPending}
            >
              ← Torna al login
            </button>
          </>
        )}
      </div>
    </div>
  )
}
